import {
  Autocomplete,
  type AutocompleteProps,
  TextField,
  type TextFieldProps,
} from '@mui/material';
import type { FieldValues, UseControllerProps } from 'react-hook-form';
import { useController, useFormContext } from 'react-hook-form';

export type ListFieldProps<T> = Omit<AutocompleteProps<T, true, false, false>, 'renderInput'> &
  UseControllerProps &
  Pick<Required<AutocompleteProps<T, true, false, false>>, 'getOptionKey' | 'getOptionLabel'> &
  Pick<TextFieldProps, 'label'> & {
    defaultHelperText?: string;
  };

export const ListField = <T,>({
  name,
  rules,
  label,
  defaultHelperText,
  ...props
}: ListFieldProps<T>) => {
  const { control } = useFormContext<FieldValues>();
  const { field, fieldState } = useController({
    name,
    control,
    rules,
  });

  return (
    <Autocomplete<T, true, false, false>
      {...field}
      {...props}
      onChange={(_, value) => field.onChange(value)}
      multiple
      filterOptions={(options) => {
        const selected = new Set(field.value.map(props.getOptionKey));
        return options.filter((option) => !selected.has(props.getOptionKey(option)));
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          error={!!fieldState.error}
          helperText={fieldState.error?.message ?? defaultHelperText}
        />
      )}
    />
  );
};
